import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, Home, ListChecks, Plus, User, LogOut, Shield } from "lucide-react";
import { Logo } from "./Logo";
import LangToggle from "./LangToggle";
import ShareApp from "./ShareApp";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (active) setUnread(data.filter((n) => !n.read).length);
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  const onLogout = () => { logout(); navigate("/"); };
  const isAdmin = user?.role === "admin";
  const homePath = user ? (isAdmin ? "/admin" : user.role === "helper" ? "/dashboard/helper" : "/dashboard/client") : "/";

  return (
    <div className="min-h-screen bg-canvas pb-24 md:pb-8">
      <header className="bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]/70 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to={homePath} data-testid="nav-home-link" className="flex items-center gap-2">
            <Logo size={36} />
            <span className="font-heading font-black text-navy text-lg tracking-tight hidden sm:inline">Go Helper</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {user && !isAdmin && (
              <>
                <NavLink to={homePath} className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-dashboard">Tableau</NavLink>
                {user.role === "client" && (
                  <NavLink to="/tasks/new" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-new-task">Nouvelle tâche</NavLink>
                )}
                {user.role === "helper" && (
                  <NavLink to="/tasks" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-feed">Tâches</NavLink>
                )}
                <NavLink to="/history" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-history">Historique</NavLink>
                <NavLink to="/calendar" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-calendar">Calendrier</NavLink>
                {user.role === "helper" && (
                  <NavLink to="/equipement" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-gear">Équipement</NavLink>
                )}
                <NavLink to="/referral" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-referral">Parrainage</NavLink>
                <NavLink to="/help" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold ${isActive ? "bg-navy text-white" : "text-navy hover:bg-surface"}`} data-testid="nav-help">Aide</NavLink>
              </>
            )}
            {isAdmin && (
              <NavLink to="/admin" className="px-4 py-2 rounded-full text-sm font-bold bg-navy text-lime" data-testid="nav-admin">Admin</NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <ShareApp variant="icon" />
            <LangToggle />
            {user && (
              <Link to="/notifications" className="relative p-2 rounded-full hover:bg-surface" data-testid="nav-notifications">
                <Bell size={20} className="text-navy" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-lime text-navy text-[10px] font-black rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="unread-count">
                    {unread}
                  </span>
                )}
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="user-menu" className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center hover:bg-[#0F2342] transition-colors">
                  {user.full_name?.[0]?.toUpperCase() || "U"}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-heading">{user.full_name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" data-testid="menu-profile"><User size={16} className="mr-2" /> Profil</Link>
                  </DropdownMenuItem>
                  {user.role === "helper" && (
                    <DropdownMenuItem asChild>
                      <Link to="/badges" data-testid="menu-badges"><Shield size={16} className="mr-2" /> Badges</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="text-red-600">
                    <LogOut size={16} className="mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="btn-primary py-2 px-4 text-sm" data-testid="nav-login">Connexion</Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10 pb-24 md:pb-12 animate-fade-up">{children}</main>

      {user && !isAdmin && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E2E8F0] z-40 pb-safe">
          <div className="max-w-6xl mx-auto grid grid-cols-5 items-center relative">
            <NavLink to={homePath} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-3 text-xs font-bold ${isActive ? "text-navy" : "text-[#94A3B8]"}`} data-testid="bottom-nav-home">
              <Home size={20} /> Accueil
            </NavLink>
            <NavLink to={user.role === "helper" ? "/tasks" : "/history"} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-3 text-xs font-bold ${isActive ? "text-navy" : "text-[#94A3B8]"}`} data-testid="bottom-nav-feed">
              <ListChecks size={20} /> {user.role === "helper" ? "Tâches" : "Historique"}
            </NavLink>
            {user.role === "client" ? (
              <Link to="/tasks/new" className="flex items-center justify-center" data-testid="fab-new-task">
                <span className="h-14 w-14 -mt-6 rounded-full bg-lime text-navy shadow-lg flex items-center justify-center ring-4 ring-white active:scale-95 transition-transform">
                  <Plus size={28} strokeWidth={3} />
                </span>
              </Link>
            ) : (
              <div />
            )}
            <NavLink to="/notifications" className={({ isActive }) => `flex flex-col items-center gap-0.5 py-3 text-xs font-bold relative ${isActive ? "text-navy" : "text-[#94A3B8]"}`} data-testid="bottom-nav-notifs">
              <Bell size={20} /> Alertes
              {unread > 0 && <span className="absolute top-2 right-6 bg-lime text-navy text-[10px] font-black rounded-full min-w-[16px] h-4 px-1">{unread}</span>}
            </NavLink>
            <NavLink to="/profile" className={({ isActive }) => `flex flex-col items-center gap-0.5 py-3 text-xs font-bold ${isActive ? "text-navy" : "text-[#94A3B8]"}`} data-testid="bottom-nav-profile">
              <User size={20} /> Profil
            </NavLink>
          </div>
        </nav>
      )}
    </div>
  );
}
